/* import __COLOCATED_TEMPLATE__ from './refund-order-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';

import type Session from 'embercom/services/session';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import type { ShopifyOrder } from '../shopify';
import { taskFor } from 'ember-concurrency-ts';

interface Signature {
  Args: {
    onModalClose: () => void;
    isOpen: boolean;
    order: ShopifyOrder;
    shopifyDomain: string | null;
    onRefundSuccess?: () => void;
  };
}

export default class RefundOrderModal extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare intercomEventService: any;

  @tracked note = '';
  @tracked restockType = 'cancel';
  @tracked restockItem = true;
  @tracked notifyCustomer = false;

  get currencySymbol() {
    return this.args.order.total?.charAt(0) ?? '$';
  }

  get refundAmount() {
    return this.args.order.total?.substring(1) ?? '0.00';
  }

  totalRefundQuantity(quantity: number) {
    return `${quantity} / ${quantity}`;
  }

  get restockTypeOptions() {
    return [
      {
        value: 'cancel',
        text: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.restock-type-options.cancel',
        ),
      },
      {
        value: 'return',
        text: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.restock-type-options.return',
        ),
      },
    ];
  }

  get isRefundOrderTaskRunning() {
    return taskFor(this.refundOrderTask).isRunning;
  }

  @action
  resetModalData() {
    this.note = '';
    this.restockType = 'cancel';
    this.restockItem = true;
    this.notifyCustomer = false;
  }

  @action
  handleModalClose() {
    this.resetModalData();
    this.args.onModalClose();
  }

  @restartableTask
  *refundOrderTask(): TaskGenerator<void> {
    try {
      let params = {
        app_id: this.session.workspace.id,
        order_id: this.args.order.id,
        shopify_domain: this.args.shopifyDomain,
        note: this.note,
        restock_type: this.restockType,
        restock_item: this.restockItem,
        notify_customer: this.notifyCustomer,
      };

      yield ajax({
        url: '/ember/inbox/shopify/refund_order',
        type: 'POST',
        data: JSON.stringify(params),
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'inbox.conversation-details-sidebar.shopify.refund-order-modal.success-message',
        ),
      );

      this.handleModalClose();

      if (this.args.onRefundSuccess) {
        this.args.onRefundSuccess();
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: 'refund_order',
        object: 'shopify_order_action',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        restock_type: this.restockType,
      });
    } catch (error) {
      captureException(error);
      let response = error?.jqXHR?.responseJSON?.error;

      if (response?.message) {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t(
            'inbox.conversation-details-sidebar.shopify.refund-order-modal.detailed-error-message',
            { message: response.message },
          ),
        });
      } else {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t(
            'inbox.conversation-details-sidebar.shopify.refund-order-modal.error-message',
          ),
        });
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::RefundOrderModal': typeof RefundOrderModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/refund-order-modal': typeof RefundOrderModal;
  }
}
